/**
 * POPULATION-EDIT-MODAL
 * Update population
 */
import Link from "@/components/link"
import Modal from "@/components/modal"
import { useEffect, useState } from "react"
import TextInput from "@/components/text-input"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import populationStore from "../../model/population.store"
import { createEmptyPopulation } from "../../model/population.types"
import PopulationDeleteModal from "./PopulationDeleteModal"
import ComingSoonModal from "@/modals/coming-soon.modal"
import siteStore from "@/entities/site/model/site.store"
import PhysicalRiskUnitsSelector, {
  PhysicalRiskUnitContext
} from "@/entities/physical-risk/ui/PhysicalRiskUnitsSelector"

interface Props {
  isNew?: boolean
  onClose: () => void
}

export default function PopulationEditModal(props: Props) {
  const dispatch = useAppDispatch()

  //Selectors
  const status = useAppSelector(populationStore.selectors.status)
  const active = useAppSelector(populationStore.selectors.active)
  const draft = useAppSelector(populationStore.selectors.draft)
  const isEdited = useAppSelector(populationStore.selectors.isEdited)
  const sites = useAppSelector(siteStore.selectors.list)

  //State
  const [modal, setModal] = useState<null | "MODAL_DELETE" | "MODAL_COMING_SOON">(null)

  //Create entity if new
  useEffect(() => {
    if (props.isNew) {
      dispatch(populationStore.thunks.createEntity(createEmptyPopulation())).unwrap()
    }
  }, [])

  //Close the modal
  async function handleClose() {
    dispatch(populationStore.actions.cancel())
    if (props.isNew) {
      await dispatch(populationStore.thunks.removeEntity(draft.id)).unwrap()
    }
    props.onClose()
  }

  //Cancel edit
  function handleCancel() {
    dispatch(populationStore.actions.cancel())
  }

  async function handleNext() {
    await dispatch(populationStore.thunks.updateEntity())
    props.onClose()
  }

  function handleSiteSelect(unit: PhysicalRiskUnitContext) {
    if (unit.type !== "site") return
    const isSelected = unit.site.populationIds.includes(draft.id)
    if (isSelected) {
      dispatch(siteStore.thunks.removePopulation({ PopulationId: draft.id, SiteId: unit.site.id }))
    } else {
      dispatch(siteStore.thunks.addPopulation({ PopulationId: draft.id, SiteId: unit.site.id }))
    }
  }

  return (
    <Modal
      isLarge
      status={status}
      isCloseButtonVisible
      onDelete={props.isNew ? undefined : () => setModal("MODAL_DELETE")}
      onClose={() => handleClose()}
      onCancel={isEdited ? () => handleCancel() : undefined}
      title={props.isNew ? "Ajouter une fonction" : `Mettre à jour ${active.name}`}
      disableClickOutside
      onNext={isEdited ? () => handleNext() : undefined}>
      {modal === "MODAL_DELETE" && <PopulationDeleteModal onClose={() => setModal(null)} />}
      {modal === "MODAL_COMING_SOON" && <ComingSoonModal onClose={() => setModal(null)} />}
      <TextInput
        title="Nom de la fonction"
        value={draft.name}
        onChange={(e) => dispatch(populationStore.actions.edit({ key: "name", value: e.value }))}
        autoFocus
      />
      <div className="h-10" />
      <Link isWithoutMargin onClick={() => setModal("MODAL_COMING_SOON")}>
        Associer avec les fonctions du SIRH
      </Link>
      <div className="h-20" />
      <PhysicalRiskUnitsSelector
        isMulti
        unitType="site"
        selectedUnits={sites
          .filter((site) => site.populationIds.includes(draft.id))
          .map((site) => ({ type: "site" as const, site }))}
        onSelect={handleSiteSelect}
      />
      <div className="h-40" />
    </Modal>
  )
}
